import {
  Button,
  Checkbox,
  Icon,
  Input,
  Modal,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import {
  machineAccounts,
  type MachineAccount,
  type MachineAccountSecret,
  type CreatedSecret,
} from "../../api/machineAccounts.service";
import { DataTable } from "../../components/DataTable/DataTable";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import { UnauthorizedView } from "../../components/UnauthorizedView";
import PermissionGate from "../../components/Permissions/PermissionGate";
import { ConfirmationModal } from "../../components/Modal/ConfirmationModal";
import { formatDateTimeShort } from "../../utils/dateFormatter";
import { rolesData } from "@legal-500/permissions";
import styles from "./MachineAccounts.module.css";

const AVAILABLE_PLATFORM_ROLES = rolesData
  .filter(role => role.context === "staff")
  .map(role => role.name)
  .sort();

type MachineAccountDetailContext = {
  setToast?: (toast: { message: string; type: ToastType } | null) => void;
};

function getSecretStatus(secret: MachineAccountSecret): string {
  if (secret.revokedAt) return "revoked";
  if (secret.expiresAt && new Date(secret.expiresAt) < new Date()) return "expired";
  return "active";
}

function getSecretStatusBadgeClass(status: string): string {
  switch (status) {
    case "active":
      return styles.secretActive;
    case "revoked":
      return styles.secretRevoked;
    default:
      return styles.secretExpired;
  }
}

function MachineAccountDetail() {
  const { id } = useParams<{ id: string }>();
  const context = useOutletContext<MachineAccountDetailContext>();

  const [account, setAccount] = useState<MachineAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [editedName, setEditedName] = useState("");
  const [isSavingInfo, setIsSavingInfo] = useState(false);

  const [currentRoles, setCurrentRoles] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isSavingRoles, setIsSavingRoles] = useState(false);

  const [secrets, setSecrets] = useState<MachineAccountSecret[]>([]);
  const [secretsLoading, setSecretsLoading] = useState(true);
  const [isCreateSecretModalOpen, setIsCreateSecretModalOpen] = useState(false);
  const [newSecretLabel, setNewSecretLabel] = useState("");
  const [isCreatingSecret, setIsCreatingSecret] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<CreatedSecret | null>(null);
  const [secretToRevoke, setSecretToRevoke] = useState<MachineAccountSecret | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  useEffect(() => {
    if (id) {
      loadAccount(id);
      loadSecrets(id);
    }
  }, [id]);

  async function loadAccount(accountId: string) {
    try {
      setLoading(true);
      setError(null);
      const [accountData, roleData] = await Promise.all([
        machineAccounts.getById(accountId),
        machineAccounts.getRoles(accountId),
      ]);
      setAccount(accountData);
      setEditedName(accountData.name);
      setCurrentRoles(roleData);
      setSelectedRoles(roleData);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load machine account"));
    } finally {
      setLoading(false);
    }
  }

  async function loadSecrets(accountId: string) {
    try {
      setSecretsLoading(true);
      const secretList = await machineAccounts.getSecrets(accountId);
      setSecrets(secretList);
    } catch (err) {
      setSecrets([]);
      showToast(
        `Failed to load secrets: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSecretsLoading(false);
    }
  }

  function showToast(message: string, type: ToastType) {
    setToast({ message, type });
    context.setToast?.({ message, type });
  }

  async function handleSaveInfo() {
    if (!id || !account) return;
    setIsSavingInfo(true);
    try {
      const updated = await machineAccounts.update(id, { name: editedName });
      setAccount(updated);
      showToast("Account updated successfully", "success");
    } catch (err) {
      showToast(
        `Failed to update: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setIsSavingInfo(false);
    }
  }

  function handleRoleToggle(role: string) {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role],
    );
  }

  async function handleSaveRoles() {
    if (!id) return;
    setIsSavingRoles(true);
    try {
      await machineAccounts.setRoles(id, "staff", selectedRoles);
      setCurrentRoles(selectedRoles);
      showToast("Roles updated successfully", "success");
    } catch (err) {
      showToast(
        `Failed to update roles: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setIsSavingRoles(false);
    }
  }

  function handleOpenCreateSecret() {
    setNewSecretLabel("");
    setIsCreateSecretModalOpen(true);
  }

  function handleCloseCreateSecret() {
    setIsCreateSecretModalOpen(false);
    setNewSecretLabel("");
  }

  async function handleCreateSecret() {
    if (!id) return;
    setIsCreatingSecret(true);
    try {
      const newSecret = await machineAccounts.createSecret(id, newSecretLabel || undefined);
      setCreatedSecret(newSecret);
      setIsCreateSecretModalOpen(false);
      setNewSecretLabel("");
      await loadSecrets(id);
    } catch (err) {
      showToast(
        `Failed to create secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setIsCreatingSecret(false);
    }
  }

  function handleCopySecret(secretValue: string) {
    navigator.clipboard
      .writeText(secretValue)
      .then(() => showToast("Secret copied to clipboard", "success"))
      .catch(() => showToast("Failed to copy secret", "error"));
  }

  function handleCopyId(idValue: string) {
    navigator.clipboard
      .writeText(idValue)
      .then(() => showToast("Account ID copied to clipboard", "success"))
      .catch(() => showToast("Failed to copy", "error"));
  }

  async function handleRevokeSecret() {
    if (!id || !secretToRevoke) return;
    try {
      await machineAccounts.revokeSecret(id, secretToRevoke.id);
      setSecretToRevoke(null);
      await loadSecrets(id);
      showToast("Secret revoked", "success");
    } catch (err) {
      showToast(
        `Failed to revoke secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
      setSecretToRevoke(null);
    }
  }

  const secretColumns: Column<MachineAccountSecret>[] = [
    { header: "Label", accessor: "label" },
    {
      header: "Created",
      accessor: "createdAt",
      render: (_value: unknown, secret: MachineAccountSecret) =>
        formatDateTimeShort(secret.createdAt),
    },
    {
      header: "Last Used",
      accessor: "lastUsedAt",
      render: (_value: unknown, secret: MachineAccountSecret) =>
        secret.lastUsedAt ? formatDateTimeShort(secret.lastUsedAt) : "Never",
    },
    {
      header: "Status",
      accessor: "revokedAt",
      render: (_value: unknown, secret: MachineAccountSecret) => {
        const status = getSecretStatus(secret);
        return (
          <span className={`${styles.secretBadge} ${getSecretStatusBadgeClass(status)}`}>
            {status}
          </span>
        );
      },
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_value: unknown, secret: MachineAccountSecret) => {
        const status = getSecretStatus(secret);
        if (status !== "active") return null;
        return (
          <PermissionGate permission="platform.write">
            <Button
              variant="outlined"
              color="default"
              onClick={e => {
                e.stopPropagation();
                setSecretToRevoke(secret);
              }}
            >
              Revoke
            </Button>
          </PermissionGate>
        );
      },
    },
  ];

  if (loading) return <LoadingState />;

  if (error) {
    const status = (error as Error & { status?: number }).status;
    if (status === 401 || status === 403) return <UnauthorizedView />;
    return (
      <EmptyState
        variant="error"
        title="Failed to load"
        message={`Failed to load machine account: ${error.message}`}
      />
    );
  }

  if (!account)
    return <EmptyState variant="empty" title="Not found" message="Machine account not found" />;

  const rolesChanged =
    JSON.stringify([...selectedRoles].sort()) !== JSON.stringify([...currentRoles].sort());

  return (
    <div className={styles.detailWrapper}>
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Account Info</h2>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Account ID</span>
            <div className={styles.infoIdRow}>
              <span className={styles.infoIdValue}>{account.id}</span>
              <Button
                variant="outlined"
                color="default"
                onClick={() => handleCopyId(account.id)}
                aria-label="Copy account id"
              >
                <Icon icon="copy" size="small" />
                Copy
              </Button>
            </div>
            <span className={styles.infoCaption}>
              Public client id — safe to share. Not the secret.
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Status</span>
            <span
              className={`${styles.statusBadge} ${account.status === "verified" ? styles.statusVerified : styles.statusSuspended}`}
            >
              {account.status}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Email</span>
            <span className={styles.infoValue}>{account.email}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Created</span>
            <span className={styles.infoValue}>{formatDateTimeShort(account.createdAt)}</span>
          </div>
        </div>
        <div className={styles.formField} style={{ marginTop: "1rem" }}>
          <label className={styles.formLabel} htmlFor="account-name">
            Name
          </label>
          <Input
            id="account-name"
            value={editedName}
            onChange={e => setEditedName(e.target.value)}
          />
        </div>
        <PermissionGate permission="platform.write">
          <div className={styles.formActions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveInfo}
              disabled={isSavingInfo || editedName === account.name}
            >
              {isSavingInfo ? "Saving..." : "Save"}
            </Button>
          </div>
        </PermissionGate>
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Platform Roles</h2>
        <div className={styles.rolesGrid}>
          {AVAILABLE_PLATFORM_ROLES.map(role => (
            <label key={role} className={styles.checkboxRow}>
              <Checkbox
                checked={selectedRoles.includes(role)}
                onChange={() => handleRoleToggle(role)}
                label={role}
              />
            </label>
          ))}
        </div>
        <PermissionGate permission="platform.write">
          <div className={styles.formActions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveRoles}
              disabled={isSavingRoles || !rolesChanged}
            >
              {isSavingRoles ? "Saving..." : "Save Roles"}
            </Button>
          </div>
        </PermissionGate>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
            Secrets
          </h2>
          <PermissionGate permission="platform.write">
            <Button variant="contained" color="primary" onClick={handleOpenCreateSecret}>
              <Icon icon="plus" size="small" />
              Create Secret
            </Button>
          </PermissionGate>
        </div>
        {secretsLoading ? (
          <LoadingState />
        ) : (
          <DataTable
            data={secrets}
            columns={secretColumns}
            emptyMessage="No secrets. Create one to allow this machine account to authenticate."
            rowKey="id"
          />
        )}
      </div>

      {isCreateSecretModalOpen && (
        <Modal
          isOpen={isCreateSecretModalOpen}
          onClose={handleCloseCreateSecret}
          title="Create Secret"
          size="small"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="secret-label">
                Label (optional)
              </label>
              <Input
                id="secret-label"
                value={newSecretLabel}
                onChange={e => setNewSecretLabel(e.target.value)}
                placeholder="e.g. production-key"
                onKeyDown={e => {
                  if (e.key === "Enter") handleCreateSecret();
                }}
              />
            </div>
            <div className={styles.formActions}>
              <Button
                variant="outlined"
                color="default"
                onClick={handleCloseCreateSecret}
                disabled={isCreatingSecret}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateSecret}
                disabled={isCreatingSecret}
              >
                {isCreatingSecret ? "Creating..." : "Create"}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {createdSecret && (
        <Modal
          isOpen={true}
          onClose={() => setCreatedSecret(null)}
          title="Secret Created"
          size="medium"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div className={styles.secretWarning}>
              This secret will only be shown once. Copy it now and store it securely.
            </div>
            <div className={styles.secretValueDisplay}>
              <span className={styles.secretValueText}>{createdSecret.secret}</span>
              <Button
                variant="outlined"
                color="default"
                onClick={() => handleCopySecret(createdSecret.secret)}
              >
                <Icon icon="copy" size="small" />
                Copy
              </Button>
            </div>
            <div className={styles.formActions}>
              <Button variant="contained" color="primary" onClick={() => setCreatedSecret(null)}>
                Done
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {secretToRevoke && (
        <ConfirmationModal
          title="Revoke Secret"
          message={`Are you sure you want to revoke the secret "${secretToRevoke.label}"?`}
          warningMessage="This action cannot be undone. Any services using this secret will no longer be able to authenticate."
          confirmButtonText="Revoke"
          confirmButtonColor="error"
          onClose={() => setSecretToRevoke(null)}
          onConfirm={handleRevokeSecret}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default MachineAccountDetail;
